import React, { useState, useRef, useEffect } from "react";
import {
  Form,
  Button,
  Typography,
  Col,
  Row,
  Modal,
  InputNumber,
  message,
  Divider,
  TimePicker,
  Spin,
  Input,
} from "antd";
import btnStyles from "../../../../../styles/Custom/Button.module.css";
import { AudioOutlined } from "@ant-design/icons";
import { vitalValidations } from "../../../../../helpers/medicineKeys";
import {
  addVitalsMonitoringApi,
  processAudioGrbsApi,
  processAudioVitalsApi,
  updateNurseVitalsMonitoringApi,
} from "../../../../../services/nurse.services";
import useQuery from "../../../../../hooks/useQuery";
import useCompactMonitoringLayout from "../../../../../hooks/useCompactMonitoringLayout";
import useTabletMonitoringLayout from "../../../../../hooks/useTabletMonitoringLayout";
import { NURSE_IP_COMPACT_MODAL_WRAP } from "../../../../IpEMR/Doctor/InitialInspection/ClosePopup";
import dayjs from "dayjs";
import AiVoiceRecording from "../AiVoiceRecording";
import { grbsFormItems, vitalFormItems } from "./vitalGrbsCols";
const format = "hh:mm A";
const getDisabledTime = () => {
  const now = dayjs();

  return {
    disabledHours: () => {
      const hours = [];
      for (let i = now.hour() + 1; i < 24; i++) {
        hours.push(i);
      }
      return hours;
    },

    disabledMinutes: (selectedHour) => {
      if (selectedHour === now.hour()) {
        const minutes = [];
        for (let i = now.minute() + 1; i < 60; i++) {
          minutes.push(i);
        }
        return minutes;
      }
      return [];
    },

    disabledSeconds: () => [],
  };
};

const AddVitalGrbs = ({ open, setOpen, editData, refresh, date, type }) => {
  const params = useQuery();
  const [loading, setLoading] = useState(false);
  const [startRecord, setStartRecord] = useState(false);
  const [recordedFile, setRecordedFile] = useState(null);
  const [showRecord, setShowRecord] = useState(false);
  const [recordLoading, setRecordLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [showButton, setShowButton] = useState(true);
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const stopRecordingRef = useRef(null);
  const isCompact = useCompactMonitoringLayout();
  const isTablet = useTabletMonitoringLayout();

  useEffect(() => {
    const baseData = editData || {}; // existing form data
    const aiData = aiResponse || {}; // new ai data

    // Merge intelligently: keep existing values if AI gives 0
    const mergedValues = { ...baseData };

    Object.keys(aiData).forEach((key) => {
      if (
        aiData[key] !== 0 && // AI really changed value
        aiData[key] !== null &&
        aiData[key] !== "" &&
        aiData[key] !== undefined
      ) {
        mergedValues[key] = aiData[key];
      }
    });

    form.setFieldsValue({
      ...mergedValues,
      action_time: mergedValues?.action_time
        ? dayjs.utc(mergedValues.action_time)
        : null,
    });
    if (editData) {
      form.setFieldsValue({
        ...mergedValues,
        action_time: mergedValues?.action_time
          ? dayjs.utc(mergedValues.action_time)
          : null,
      });
    } else if (open) {
      form.resetFields();
      form.setFieldsValue({
        action_time: dayjs(),
      });
    }
  }, [aiResponse, editData, open]);

  // Cleanup recordings when modal closes
  useEffect(() => {
    if (!open) {
      setStartRecord(false);
      if (stopRecordingRef.current) {
        stopRecordingRef.current();
      }
      // Reset recording states
      setShowRecord(false);
      setRecordedFile(null);
      setShowButton(true);
    }
  }, [open]);

  const onFinish = (values) => {
    setLoading(true);
    const selectedTime = values?.action_time
      ? dayjs(values?.action_time).format("HH:mm")
      : null;
    const combinedDateTime = selectedTime
      ? dayjs(date)
          .set("hour", dayjs(values?.action_time).hour())
          .set("minute", dayjs(values?.action_time).minute())
          .format("YYYY-MM-DDTHH:mm:ss")
      : editData?.action_time;
    const grbsFlag = type === "grbs" && { is_grbs: true };
    const updatedValues = {
      ...values,
      action_time: combinedDateTime,
      ...grbsFlag,
    };

    // Stop recording before submitting
    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }

    if (editData?.id) {
      updateNurseVitalsMonitoringApi(editData?.id, updatedValues)
        ?.then((res) => {
          message.success(res?.data?.message);
          setOpen(false);
          setLoading(false);
          refresh();
        })
        .catch((err) => {
          setLoading(false);
          message?.error(err?.response?.data?.message);
        });
    } else {
      addVitalsMonitoringApi(params?.patient, updatedValues)
        ?.then((res) => {
          message.success(res?.data?.message);
          setOpen(false);
          setLoading(false);
          refresh();
        })
        .catch((err) => {
          message?.error(err?.response?.data?.message);
          setLoading(false);
        });
    }
  };

  const onRecordSubmit = async (values) => {
    const combinedDateTime = editData?.action_time
      ? editData?.action_time
      : null;

    if (!recordedFile) {
      message.error("No recording found");
      return;
    }

    // Stop recording before processing
    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }

    setRecordLoading(true);
    try {
      const fd = new FormData();
      fd.append("audio_file", recordedFile);
      fd.append("action_time", JSON.stringify(combinedDateTime));
      const response =
        type === "vitals"
          ? await processAudioVitalsApi(fd)
          : await processAudioGrbsApi(fd);

      setAiResponse(response?.data || {});
      setShowRecord(false);
      setShowButton(true);
      message.success("Audio processed successfully!");
    } catch (error) {
      console.error(error);
      message.error("Failed to process audio.");
    } finally {
      setRecordLoading(false);
    }
  };

  const handleCancel = () => {
    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }
    setOpen(false);
  };

  const commonFormItems = type === "vitals" ? vitalFormItems : grbsFormItems;

  return (
    <div>
      <Modal
        open={open}
        onCancel={isCompact ? handleCancel : undefined}
        closeIcon={isCompact ? undefined : null}
        width={
          isTablet
            ? "min(720px, calc(100vw - 48px))"
            : isCompact
              ? "calc(100vw - 32px)"
              : "min(720px, calc(100vw - 48px))"
        }
        centered
        wrapClassName={isCompact ? NURSE_IP_COMPACT_MODAL_WRAP : undefined}
        getContainer={isCompact ? () => document.body : undefined}
        className={[
          "nurse-monitoring-vitals-modal",
          isTablet ? "nurse-monitoring-vitals-modal--tablet" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        destroyOnClose={isCompact || undefined}
        title={
          isCompact ? (
            editData?.id
              ? `Edit ${type === "grbs" ? "GRBS" : "Vitals"}`
              : `Add ${type === "grbs" ? "GRBS" : "Vitals"}`
          ) : (
            <Row>
              <Col span={12} className="flex-start">
                <Typography
                  style={{
                    marginLeft: "12px",
                    fontWeight: "400",
                    fontSize: "20px",
                  }}
                >
                  {editData?.id
                    ? ` Edit ${type === "grbs" ? "GRBS" : "Vitals"}`
                    : ` Add ${type === "grbs" ? "GRBS" : "Vitals"}`}
                </Typography>
              </Col>
            </Row>
          )
        }
        maskClosable={false}
        keyboard={false}
        footer={
          <>
            <Divider
              className={isCompact ? "nurse-monitoring-modal-footer-divider" : undefined}
              style={{ margin: "16px 0" }}
            />

            <div
              className={
                isCompact
                  ? [
                      "nurse-monitoring-modal-footer",
                      isTablet ? "nurse-monitoring-modal-footer--tablet" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")
                  : undefined
              }
              style={{
                display: "flex",
                justifyContent: editData?.id ? "space-between" : "flex-end",
                alignItems: "center",
              }}
            >
              {editData?.id && (
                <Button
                  type="primary"
                  icon={<AudioOutlined />}
                  size={isCompact ? "middle" : "large"}
                  className={isCompact ? "nurse-monitoring-ai-btn" : undefined}
                  onClick={() => {
                    setShowButton(false);
                    setStartRecord(true);
                    setShowRecord(true);
                  }}
                  disabled={!showButton}
                >
                  AI Voice Assistant
                </Button>
              )}

              <div
                className={isCompact ? "nurse-monitoring-modal-actions" : undefined}
                style={{ display: "flex", gap: "16px" }}
              >
                <Form.Item style={{ margin: 0 }}>
                  <Button
                    type="primary"
                    ghost
                    className={`${btnStyles.mdBtn} inv-btn`}
                    onClick={handleCancel}
                    disabled={loading || recordLoading}
                  >
                    Cancel
                  </Button>
                </Form.Item>

                <Form.Item style={{ margin: 0 }}>
                  <Button
                    disabled={recordedFile && !aiResponse}
                    loading={loading}
                    htmlType="submit"
                    type="primary"
                    className={`${btnStyles.mdBtn} inv-btn`}
                    onClick={() => {
                      if (formRef.current) {
                        formRef.current.submit();
                      }
                    }}
                  >
                    Save
                  </Button>
                </Form.Item>
              </div>
            </div>
          </>
        }
      >
        <Divider
          className={isCompact ? "nurse-monitoring-modal-top-divider" : undefined}
          style={{ margin: "16px 0" }}
        />
        {recordLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" tip="Processing..." />
          </div>
        ) : (
          <Form layout="vertical" onFinish={onFinish} form={form} ref={formRef}>
            <Form.Item
              className={isTablet ? "nurse-monitoring-time-item" : undefined}
              label="Time"
              name="action_time"
              rules={[{ required: true, message: "Please Select time" }]}
            >
              <TimePicker
                inputReadOnly
                format={format}
                needConfirm={isCompact}
                disabledTime={getDisabledTime}
                style={{ width: "100%" }}
                placement="bottomLeft"
                popupClassName={isCompact ? "curengo-mobile-time-picker" : undefined}
                getPopupContainer={isCompact ? () => document.body : undefined}
              />
            </Form.Item>
            <Row gutter={[12, 0]} className="nurse-monitoring-form-grid">
              {commonFormItems?.map((item) => (
                <Col xs={24} sm={12} key={item.value}>
                  <Form.Item label={item?.name} name={item?.value}>
                    {item?.value === "remarks" ? (
                      <Input.TextArea />
                    ) : item?.value === "insulin" ? (
                      <InputNumber style={{ width: "100%" }} />
                    ) : (
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder={`${
                          vitalValidations[item?.value]?.minimum
                        }-${vitalValidations[item?.value]?.maximum}`}
                      />
                    )}
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Form>
        )}

        {showRecord && !recordLoading && (
          <AiVoiceRecording
            setRecordedFile={setRecordedFile}
            startRecord={startRecord}
            setStartRecord={setStartRecord}
            onRecordSubmit={onRecordSubmit}
            stopAllRecordings={stopRecordingRef}
            type={type}
          />
        )}
      </Modal>
    </div>
  );
};

export default AddVitalGrbs;
